import { Injectable } from '@angular/core';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';
import { LoggerService } from '@content-platform/web-angular-shared-util-logging';
import { Client } from '@microsoft/microsoft-graph-client';
import {
  UserAgentApplication,
  AuthenticationParameters,
  AuthResponse,
  Configuration as AuthConfig
} from 'msal';
import { filter, switchMap, take } from 'rxjs/operators';
import { AUTH_AUTHORITY } from './auth-api.config';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { ERROR_PATHS } from '@content-platform/web-angular-shared-util-error-handling';
import { Router } from '@angular/router';
import { SCOPE } from './msal.service.config';
import { Store } from '@ngrx/store';
import { 
  getADUser, 
  getAuthenticationStrategy, 
  getGraphToken 
} from './auth/auth.selectors';
import { AuthStrategy } from './auth/auth.interfaces';

const tokens: { [resource: string]: string } = {};
const tokens$: Subject<{ [resource: string]: string }> = new BehaviorSubject(tokens);
@Injectable({
  providedIn: 'root'
})
export class MSALService {
  graphClient: Client;
  logger: LoggerService;
  authContext: UserAgentApplication;

  constructor(private loggerService: LoggerService, protected router: Router, private store: Store) {
    this.constructMsalService();
    this.logger = this.loggerService.instance('MsGraphApiService');
  }

  private constructMsalService() {
    Configuration.getAsync('adal')
      .pipe(
        filter(config => !!config),
        take(1)
      )
      .subscribe(adalConfig => {
        const config = this.getAuthConfig(adalConfig.clientId);

        this.authContext = new UserAgentApplication(config);

        this.handleCallback();

        this.graphClient = this.initializeGraphClient();
      });
  }

  /**
   * Returns the authentication config needed to create the auth context
   * @param clientId
   */
  private getAuthConfig(clientId: string): AuthConfig {
    const redirectUri = window.location.href || window.location.origin;

    return {
      auth: {
        clientId: clientId,
        authority: AUTH_AUTHORITY,
        validateAuthority: true,
        redirectUri: redirectUri
      }
    };
  }

  /**
   * Initialize the Graph client with an auth provider that requests the token from the auth service
   */
  private initializeGraphClient(): Client {
    return (this.graphClient = Client.init({
      authProvider: async done => {
        this.store.select(getAuthenticationStrategy).subscribe((authStrategy) => {
          if (authStrategy === AuthStrategy.OKTA) {
            this.store.select(getADUser)
              .pipe(
                filter(user => !!user),
                switchMap(() => this.store.select(getGraphToken)))
              .subscribe((token) => done(null, token));
          }
          else {
            this.authContext
              .acquireTokenSilent(this.getTokenRequestParameters(SCOPE))
              .then((res: AuthResponse) => done(null, res.accessToken))
              .catch(reason => done(reason, null));
          }
        });
      },
      defaultVersion: 'beta'
    }));
  }

  private getTokenRequestParameters(scope: string): AuthenticationParameters {
    const userName = this.getUserAccount() ? this.getUserAccount().userName : null;

    return {
      loginHint: userName,
      authority: AUTH_AUTHORITY,
      scopes: [scope],
      redirectUri: `${window.location.origin}/frameRedirect.html`
    };
  }

  /**
   * Gets user's account
   * @deprecated given_name and family_name properties will be removed
   */
  public getUserAccount(): adal.User {
    let given_name: string;
    let family_name: string;

    const msalAccount = this.authContext.getAccount();

    given_name =
      msalAccount && msalAccount.idToken && msalAccount.idToken.hasOwnProperty('given_name')
        ? msalAccount.idToken['given_name']
        : 'undefFirsttName';

    family_name =
      msalAccount && msalAccount.idToken && msalAccount.idToken.hasOwnProperty('family_name')
        ? msalAccount.idToken['family_name']
        : 'undefLastName';

    return msalAccount
      ? {
          userName: msalAccount.userName,
          profile: {
            ...msalAccount.idToken,
            unique_name: msalAccount.userName,
            upn: msalAccount.userName,
            family_name: family_name,
            given_name: given_name
          }
        }
      : null;
  }

  /**
   * Gets initial Idtoken for the app backend
   * Saves the resulting Idtoken in localStorage.
   */
  login(): void {
    const loginRequest = {
      scopes: [SCOPE]
    };

    this.authContext.loginRedirect(loginRequest);
  }

  /**
   * Logout user will redirect page to logout endpoint.
   * After logout, it will redirect to post_logout page if provided.
   */
  logout(): void {
    this.authContext.logout();
  }

  /**
   * Used to acquire the azure graph token
   *
   * @param resourcePath that requires the auth token.
   * @param callback called with a message & auth token.
   */
  acquireToken(resourcePath: string, callback?: (message: string, token: string) => void): void {
    this.authContext
      .acquireTokenSilent(this.getTokenRequestParameters(resourcePath))
      .then((res: AuthResponse) => {
        if (!res.accessToken && resourcePath && /AADSTS50058/.test(resourcePath)) {
          this.router.navigate(['/' + ERROR_PATHS.TokenError], { skipLocationChange: true });
        }

        this.updateTokenObservable(resourcePath, res.accessToken);

        if (callback) {
          callback(resourcePath, res.accessToken);
        }
      })
      .catch(reason => {
        this.logger.error(
          `Get token failed | Scope ${resourcePath} | Reason ${JSON.stringify(reason, null, 2)}`
        );
      });
  }

  /**
   * Gets an observable  of a token token for the specified resource
   * @param resource {string} the resource to get token for
   * @returns {Observable<{ [resource: string]: string }>} token
   */
  get accessTokens$(): Observable<{ [resource: string]: string }> {
    return tokens$;
  }

  /**
   * Get state for authenticated user
   */

  getisAuthenticated(): boolean {
    return !!this.authContext.getAccount();
  }

  private updateTokenObservable(
    resource: string,
    value = ''
  ): Observable<{ [resource: string]: string }> {
    tokens[resource] = value;
    tokens$.next(tokens);
    return tokens$;
  }

  handleCallback(): void {
    this.authContext.handleRedirectCallback(this.authCallback);
  }

  private authCallback(error, response) {
    if (error) {
      this.logger.error(JSON.stringify(error));
    }

    if (response) {
      this.logger.info(JSON.stringify(response));
    }
  }
}
